import { ActionReducerMapBuilder } from '@reduxjs/toolkit'
import { find, findIndex, isEmpty, map, some } from 'lodash'

import { initializeClasses, loadAllLabelingItems, loadLabelingData } from '@/stores/slices/Labeling/thunks/initialization'
import { updateComments } from '@/stores/slices/Labeling/reducers/common'
import { LabelingState } from '@/stores/slices/Labeling/types'
import { CURSOR_COLORS, OCR_GLOBAL_OPACITY } from '@/stores/slices/Labeling/const'
import { transformAnnotObjects } from '@/stores/slices/Labeling/utils'

import { LABELING_STATUS } from '@/api/LabelingNode/const'

import { ModelTypes, RUNNING_TASKS, TaskStatusTypes } from '@/pages/const'

export const initializationExtraReducers = (builder: ActionReducerMapBuilder<LabelingState>) => {
  builder.addCase(loadLabelingData.pending, (state) => {
    state.isInProgress = true
  })
  builder.addCase(loadLabelingData.fulfilled, (state, action) => {
    state.isInProgress = false

    const {
      labelingNodeId,
      labelingNodeInfo,
      userShortcuts,
      promptImages,
      userId,
      semiAutoLabelingReportList,
    } = action.payload

    state.labelingNodeId = labelingNodeId
    state.name = labelingNodeInfo.name
    state.projectId = labelingNodeInfo.projectId
    state.classGroupId = labelingNodeInfo.classGroupId
    state.numOfLabelingData = labelingNodeInfo.numOfLabelingItems || labelingNodeInfo.numOfDatasetItems
    state.modelType = labelingNodeInfo.modelType || 'ANOMALY_DETECTION'
    state.isAuto = labelingNodeInfo.isAuto || false
    state.status = labelingNodeInfo.status

    if (state.modelType === ModelTypes.OCR) {
      state.tool.opacity = OCR_GLOBAL_OPACITY * 100
    }

    state.isReviewAvailable = !isEmpty(labelingNodeInfo.reviewers)
    state.userIsReviewer = some(labelingNodeInfo.reviewers, { userId })
    state.userIsLabeler = some(labelingNodeInfo.labelingMembers, { userId })
    state.isLabelerAssigned = labelingNodeInfo.isLabelerAssigned
    state.reviewers = labelingNodeInfo.reviewers
    state.classTagFilters = []
    state.fileFilter = !isEmpty(labelingNodeInfo.reviewers)
      ? [LABELING_STATUS.NOT_STARTED, LABELING_STATUS.FINISHED, LABELING_STATUS.REJECTED, LABELING_STATUS.COMPLETED]
      : [LABELING_STATUS.NOT_STARTED, LABELING_STATUS.COMPLETED]

    state.userCursorColor = CURSOR_COLORS[Math.floor(Math.random() * 5)]
    state.hasClusteringResult = labelingNodeInfo.hasClusteringResult
    state.hasSelectedForClustering = labelingNodeInfo.hasSelectedForClustering ?? false
    state.promptImageIds = map(promptImages, (image) => image._id)
    state.userShortcuts = userShortcuts

    // 세미오토 라벨링
    state.jobStatus = labelingNodeInfo.jobStatus

    if (labelingNodeInfo.jobStatus) {
      state.isRunningSemiAutoLabeling = RUNNING_TASKS.includes(labelingNodeInfo.jobStatus)
    }

    if (labelingNodeInfo.jobStatus === TaskStatusTypes.COMPLETED) {
      state.showReportButton = true
      state.reportId = semiAutoLabelingReportList[0]._id // 가장 최신 리포트
    }

    if (semiAutoLabelingReportList.length > 1 && (labelingNodeInfo.jobStatus === TaskStatusTypes.CANCELED || labelingNodeInfo.jobStatus === TaskStatusTypes.ERROR)) {
      // 지금 jobStatus가 cancel이나 error이면 -> 이전 리포트 중에 가장 최신 완료 리포트를 찾아서 그거 아이디로 reportId 세팅
      const latestCompletedReport = find(semiAutoLabelingReportList, (report) => report.jobStatus === TaskStatusTypes.COMPLETED)
      if (latestCompletedReport) {
        state.reportId = latestCompletedReport._id
        state.showReportButton = true
      }
    }
  })
  builder.addCase(loadLabelingData.rejected, (state) => {
    state.isInProgress = false
  })
  builder.addCase(loadAllLabelingItems.pending, (state) => {
    state.isInProgress = true
  })
  builder.addCase(loadAllLabelingItems.fulfilled, (state, action) => {
    // 전체 라벨링아이템(파일) 리스트
    const labelingAllItemList = action.payload

    // locationFileId 가 있으면 해당 이미지의 인덱스를 currentIndex로 설정
    const { locationFileId = '' } = action.meta.arg

    const locationFileIdx = findIndex(labelingAllItemList, (item) => item._id === locationFileId)
    const currentIndex = locationFileIdx !== -1 ? locationFileIdx : 0

    // 현재 보고 있는 이미지
    const curImage = labelingAllItemList[currentIndex]
    // 현재 보고 있는 이미지의 annotObject 저장
    const annotation: LabelingState['annotation'] = {}
    if (curImage) {
      annotation[curImage._id] = { status: curImage.status, object: transformAnnotObjects(curImage.labelingObjects || []) }
    }

    // 전체 필터로 적용
    state.files = labelingAllItemList
    state.originalFiles = labelingAllItemList
    state.annotation = annotation
    state.currentIndex = currentIndex

    updateComments(state, action.payload)
    state.isInProgress = false
  })
  builder.addCase(initializeClasses.pending, (state) => { state.isInProgress = true })
  builder.addCase(initializeClasses.fulfilled, (state, action) => {
    state.classes = action.payload
    state.tool.class = action.payload[0]?._id || null
  })
  builder.addCase(initializeClasses.rejected, (state) => { state.isInProgress = false })
}
